import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GripVertical, RotateCcw } from "lucide-react";

const TILES = {
    sales_month: { label: "Sales this month", money: true, to: "/account/orders" },
    sales_all: { label: "Sales all time", money: true, to: "/account/orders" },
    orders_open: { label: "Open orders", to: "/account/orders" },
    unread_messages: { label: "Unread messages", to: "/account/inbox" },
    products_live: { label: "Live products", to: "/account/products" },
    listings_live: { label: "Classified ads", to: "/account/listings" },
    consignment_earned: { label: "Consignment earned", money: true, to: "/account/consignment" },
    classes_live: { label: "Classes on the board", to: "/account/classes" },
};

const DEFAULT = ["sales_month", "orders_open", "unread_messages", "products_live",
    "sales_all", "consignment_earned", "listings_live", "classes_live"];
const KEY = "backoffice.tiles";

const read = () => {
    try {
        const saved = JSON.parse(localStorage.getItem(KEY));
        if (!Array.isArray(saved)) return DEFAULT;
        const clean = saved.filter((k) => TILES[k]);
        return [...clean, ...DEFAULT.filter((k) => !clean.includes(k))];
    } catch { return DEFAULT; }
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The numbers, in whatever order this shop wants them. Drag a card, it stays put. */
export default function StatTiles({ stats }) {
    const [order, setOrder] = useState(read);
    const from = useRef(null);

    const save = useCallback((next) => {
        setOrder(next);
        try { localStorage.setItem(KEY, JSON.stringify(next)); } catch { /* private mode */ }
    }, []);

    const enter = (i) => {
        if (from.current === null || from.current === i) return;
        const next = [...order];
        next.splice(i, 0, next.splice(from.current, 1)[0]);
        from.current = i;
        setOrder(next);
    };

    return (
        <div data-testid="dash-tiles">
            <div className="mb-1 flex items-center justify-between">
                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">
                    Your numbers · drag to rearrange
                </p>
                <button onClick={() => save(DEFAULT)} data-testid="dash-tiles-reset"
                    className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase text-[#4a4a4a] hover:text-[#1a1a1a]">
                    <RotateCcw size={11} /> reset
                </button>
            </div>

            <div className="grid border-y border-l border-[#d0d0d0] sm:grid-cols-2 xl:grid-cols-4">
                {order.map((key, i) => {
                    const t = TILES[key];
                    const value = t.money ? money(stats?.[key]) : (stats?.[key] ?? 0);
                    return (
                        <div key={key} draggable data-testid={`dash-tile-${key}`}
                            onDragStart={() => { from.current = i; }}
                            onDragEnter={() => enter(i)}
                            onDragOver={(e) => e.preventDefault()}
                            onDragEnd={() => { from.current = null; save(order); }}
                            className="group relative border-r border-[#d0d0d0] bg-white px-2.5 py-2">
                            <GripVertical size={12} className="absolute right-1 top-1 cursor-grab text-[#4a4a4a] opacity-0 group-hover:opacity-100" />
                            <Link to={t.to} className="block">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4a4a4a]">{t.label}</p>
                                <p className="mt-0.5 text-[22px] font-bold leading-none tabular-nums tracking-tight text-[#1a1a1a]">{value}</p>
                            </Link>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
